import React from 'react';
import { useRouter } from '@tanstack/react-router';
import { Card, Descriptions, Space, Table, Tabs, Typography } from 'antd';
import type { TabsProps } from 'antd';
import { useAppQuery } from '@/common/hooks/useAppQuery';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import { get } from '@/common/services/httpClient';
import type { ApiResponse } from '../../../auth/types/authTypes';
import { t } from 'i18next';
import dayjs from 'dayjs';
import StatusTag from '@/common/components/Status';
import AmountFormat from '@/common/components/AmountFormat';
import type { ColumnsType } from 'antd/es/table';
import InvoicePrintButton from "../../../participants/pages/policyHolder/InvoicePrint";
import type { InvoiceLineItem, PolicyHolder, PolicyHolderDependent, SearchPolicyType, StatusHistoryType } from '../../types/policiesTypes';
import type {
    InvoiceType,
} from '../../../participants/types/participantsTypes';
import FormatLocalDate from '../../../../common/components/FormatLocalDate';
import { useTranslation } from 'react-i18next';


const Detail: React.FC = () => {
    const { state } = useRouter().state.location;
    const { t } = useTranslation();
    const record = (state as { record?: SearchPolicyType })?.record;

    const Id = record?.id?.toString() || '';
    const Code = record?.code.toString() || '';
    const { data: policyData, isLoading, isError } = useAppQuery(
        [QUERY_KEYS.POLICIES.Policy, record],
        () => fetchDetail(Id, Code),
        { enabled: !!Id },
    );
    const onChange = (key: string) => console.log('Selected Tab:', key);

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: t('Policy Holder(s)'),
            children: <PlanRow data={policyData?.policyHolder} />,
        },
        {
            key: '2',
            label: t('History'),
            children: <HistoryRow data={policyData?.statusHistory} />,
        },
        {
            key: '3',
            label: t('Invoice'),
            children: <InvoicesLineRow data={policyData?.invoiceResponse} />,
            forceRender: true,
        },
    ];
    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Company Name and Status */}
                    <Title level={3} style={{ margin: 0 }}>
                        <Space size="small">
                            {policyData?.name || t("Policy Detail")}
                        </Space>
                    </Title>

                    {/* Address and Country */}
                    <Space size="middle" wrap>
                        <Text type="secondary">
                            {policyData?.address} {policyData?.country}
                        </Text>
                    </Space>

                    {/* Contact Info */}
                    <Space size="middle" wrap>
                        <Text type="secondary">{policyData?.email}</Text>
                        <Text type="secondary">{policyData?.phoneNo}</Text>
                    </Space>
                </div>
            </Card>

            {/* Loading and Error States */}
            {isLoading && <p>Loading...</p>}
            {isError && <p>Error fetching data</p>}

            {/* Main Content */}
            {!isLoading && !isError && (
                <>
                    <InfoRow data={policyData} />
                    <Tabs
                        defaultActiveKey="1"
                        items={items}
                        onChange={onChange}
                        destroyInactiveTabPane={false}
                    />
                </>
            )}
        </div>
    );
};
// Fetch function
const fetchDetail = async (
    Id: string,
    Code: string
): Promise<SearchPolicyType> => {
    const response = await get<ApiResponse<SearchPolicyType>>(
        `${API_ENDPOINTS.POLICIES.GET_POLICY_DETAIL}?Id=${Id}&Code=${Code}`,
    );
    return response.data;
};

const { Title, Text } = Typography;


// Section container
const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div
        style={{
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: '24px',
            marginBottom: '24px',
            backgroundColor: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
    >
        <h3 style={{ marginBottom: 16 }}>{title}</h3>
        {children}
    </div>
);

// Columns
const historyColumns = [
    { title: t('Created By'), dataIndex: 'userName', key: 'userName' },
    {
        title: t('Created On'), dataIndex: 'createdOn', key: 'createdOn',
        render: (value: string) => <FormatLocalDate value={value} />,
    },
    {
        title: t('Status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
            <StatusTag status={status} />
        ),
    },
];


const invoiceColumns = [
    { title: t("Plan"), dataIndex: "planName", key: "planName" },
    { title: t('Invoice Date'), dataIndex: 'invoiceDate', key: 'invoiceDate', render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
    { title: t('Due Date'), dataIndex: 'dueDate', key: 'dueDate', render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
    {
        title: t("Total Amount"),
        dataIndex: "totalAmount",
        key: "totalAmount",
        render: (value: number, record: InvoiceType) =>
            `${value.toLocaleString()} ${record?.currencyISOCode != null ? record?.currencyISOCode : ""}`,
    },
    {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
            <StatusTag status={status} />
        ),
    },
    {
        title: t("Actions"),
        key: "actions",
        render: (_: any, record: InvoiceType) => <InvoicePrintButton invoice={record} />,
    },
];

// Line item columns
const lineItemColumns = [
    { title: t("Name"), dataIndex: "name", key: "name" },
    { title: t("Age"), dataIndex: "age", key: "age" },
    { title: t("Gender"), dataIndex: "gender", key: "gender" },
    { title: t("Category"), dataIndex: "category", key: "category" },
    {
        title: t("Amount"),
        dataIndex: "amount",
        key: "amount",
        render: (value: number, record: InvoiceLineItem) =>
            `${value.toLocaleString()} ${record?.currencyISOCode != null ? record?.currencyISOCode : ""}`,
    },
    { title: t("Effective Date"), dataIndex: "effectiveDate", key: "effectiveDate", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : '') },
    {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
            <StatusTag status={status} /> 
        ),
    },
];

const InfoRow = ({ data }: { data?: SearchPolicyType }) => (
    <Descriptions
        bordered
        size="middle"
        column={{ xs: 1, sm: 2, md: 3 }}
        labelStyle={{
            fontWeight: 600,
            width: 150,           // ✅ fixed width for labels
            whiteSpace: "nowrap", // ✅ keep label text in one line
        }}
        contentStyle={{
            wordBreak: "break-word", // ✅ wrap long data text
        }}
        style={{ marginBottom: 24 }}
    >
        <Descriptions.Item label={t("Company Name")}>
            {data?.policyHolderName || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Plan Name")}>
            {data?.planName || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label={t("Policy Code")}>
            {data?.code || "N/A"}
        </Descriptions.Item>

        <Descriptions.Item label={t("Inception Date")}>
            {data?.inceptionDate
                ? dayjs(data.inceptionDate).format("DD-MM-YYYY")
                : "N/A"}
        </Descriptions.Item>

        <Descriptions.Item label={t("Expiry Date")}>
            {data?.expiryDate
                ? dayjs(data.expiryDate).format("DD-MM-YYYY")
                : "N/A"}
        </Descriptions.Item>

        <Descriptions.Item label={t("Status")}>
            <StatusTag status={data?.status} />
        </Descriptions.Item>

        <Descriptions.Item label={t("Premium Amount")}>
            <AmountFormat
                value={data?.totalAmount}
                currencyCode={data?.currencyISOCode}
                currencyName={data?.currencyISOCode}
                amountInWord={true}
            />
        </Descriptions.Item>
    </Descriptions>
);

const PlanRow = ({ data }: { data?: PolicyHolder[] }) => {
    return (
        <Section title={t("Policy Holder(s)")}>
            <Table
                columns={policyHolderColumns}
                dataSource={data}
                rowKey="id"
                expandable={{
                    expandedRowRender: (record: PolicyHolder) => (
                        <Table<PolicyHolderDependent>
                            columns={dependentsColumns}
                            dataSource={record.policyHolderDependent}
                            rowKey="parentNationalIDNo"
                            pagination={false}
                            size="small"
                        />
                    ),
                    rowExpandable: (record) => record.policyHolderDependent?.length > 0,
                }}
                //pagination={true}
            />

        </Section>
    );
};


// Define columns for the PolicyHolder table
const policyHolderColumns: ColumnsType<PolicyHolder> = [
    {
        title: t('Card Number'),
        key: 'cardNumber',
        dataIndex: "cardNumber"
    },
    { title: t("Name"), dataIndex: "name", key: "name" },
    {
        title: t("DOB"), dataIndex: "dob", key: "dob", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
    },
    { title: t("Category"), dataIndex: "category", key: "category" },
    {
        title: t("Amount"),
        dataIndex: "premiumAmount",
        key: "premiumAmount",
        render: (value: number, record: any) => (
            <AmountFormat
                value={value}
                currencyCode={record.currencyISOCode}
                amountInWord={false}
            />
        ),
    },
    {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
            <StatusTag status={status} />
        ),
    },

];

const dependentsColumns: ColumnsType<PolicyHolderDependent> = [
    {
        title: t('Card Number'),
        key: 'cardNumber',
        dataIndex: "cardNumber"
    },
    { title: t("Name"), dataIndex: "name", key: "name" },
    {
        title: t("DOB"), dataIndex: "dob", key: "dob", render: (text: string) => (text ? dayjs(text).format('DD-MM-YYYY') : ''),
    },
    { title: t("Category"), dataIndex: "category", key: "category" },
    {
        title: t("Amount"),
        dataIndex: "premiumAmount",
        key: "premiumAmount",
        render: (value: number, record: any) => (
            <AmountFormat
                value={value}
                currencyCode={record.currencyISOCode}
                amountInWord={false}
            />
        ),
    },
    {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        render: (status: string) => (
            <StatusTag status={status} />
        ),
    },

];


const HistoryRow = ({ data }: { data?: StatusHistoryType[] }) => (
    <Section title={t('History')}>
        <Table columns={historyColumns} dataSource={data} pagination={false} />
    </Section>
);

const InvoicesLineRow = ({ data }: { data?: InvoiceType[] }) => {
    return (
        <Section title={t("Invoices")}>
            <Table
                columns={invoiceColumns}
                dataSource={data}
                rowKey="id"
                expandable={{
                    expandedRowRender: (record: InvoiceType) => (
                        <Table<InvoiceLineItem>
                            columns={lineItemColumns}
                            dataSource={record.lineItems}
                            rowKey="lineItemId"
                            pagination={{ pageSize: 10 }}
                            size="small"
                        />
                    ),
                    rowExpandable: (record) => record.lineItems?.length > 0,
                }}
                pagination={{ pageSize: 10 }}
            />

        </Section>
    );
};



export default Detail;